import { supabase } from "@/integrations/supabase/client";
import type { CompressOptions } from "@/lib/image-compression";
import { USE_S3_PROPERTY_IMAGES, USE_S3_PROPERTY_VIDEOS } from "@/lib/featureFlags";
import {
  deletePropertyMediaViaApi,
  uploadPropertyImageViaApi,
  type PropertyMediaEntityType,
} from "@/services/api";

// Dynamic import: keeps browser-image-compression (~50 KB minified) and the
// SVG watermark asset out of the main entry chunk. ~13 components import
// storage.ts purely for IMAGE_ACCEPT / uploadFile / uploadDocument, so a
// static import here would land the compression library on every page load
// AND trigger a chunk-hash cascade that bloats every deploy's rsync.
const loadCompressionModule = () => import("@/lib/image-compression");

// Same reasoning, bigger stakes: heic2any bundles a wasm libheif decoder
// (~2.7 MB unpacked). It must never load unless a HEIC/HEIF file is actually
// encountered — see convertHeicIfNeeded below, which is the only call site.
// The import promise is cached (module-scope, like loadLogo() in
// image-compression.ts) so a batch of N concurrent HEIC conversions shares
// ONE import() call instead of firing N of them in parallel.
// Exported for tests. Drops the cache on rejection so a transient chunk-load
// failure (flaky network / stale PWA cache) doesn't poison every later HEIC
// upload with the same rejected promise (#3093).
export function cacheImportUntilRejected<T>(load: () => Promise<T>): () => Promise<T> {
  let cached: Promise<T> | null = null;
  return () => {
    if (!cached) {
      cached = load().catch((error) => {
        cached = null;
        throw error;
      });
    }
    return cached;
  };
}
const loadHeicConversionModule = cacheImportUntilRejected(() => import("heic2any"));

// Bounded concurrency for multi-HEIC batches — a 20-photo iPhone drop
// shouldn't spawn 20 simultaneous wasm decodes. Mirrors compressBatch's
// concurrency default philosophy but capped lower: libheif's wasm decode is
// heavier per-call than the canvas-based compressor.
const HEIC_CONVERSION_CONCURRENCY = 3;

// C1 phase 2 (#3059, migration
// supabase/migrations/20260706150000_media_phase2_documents_bucket_company_scoped.sql):
// `media` stays public (imagery only), `documents` is the new private bucket
// for everything else (PII: contracts, receipts, licenses, maintenance, …).
const MEDIA_BUCKET = "media";
const DOCUMENTS_BUCKET = "documents";
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
/** TTL for signed URLs into the private `documents` bucket. Matches the
 * `payment-proofs` precedent (src/pages/finance/PaymentVerifications.tsx). */
const PRIVATE_URL_TTL_SECONDS = 600;

export interface UploadResult {
  url: string;
  path: string;
}

export type StorageFolder =
  | "properties"
  | "units"
  | "listings"
  | "contracts"
  | "profiles"
  | "maintenance"
  | "owners"
  | "tenants"
  | "employees"
  | "service-providers"
  | "companies"
  | "expenses"
  | "receipts"
  | "licenses"
  | "inspections"
  | "service-requests"
  | "appliances"
  // Note attachments live under `tasks/{taskId}/...` so files are
  // scoped to the task. The activity timeline stores the resulting
  // URLs in task_activity.payload (kind='note').
  | "tasks"
  | "content-pages"
  | "projects";

/**
 * Public-imagery folder allow-list — MUST stay in sync with the `media`
 * bucket's INSERT/SELECT/DELETE policy folder lists in
 * supabase/migrations/20260706150000_media_phase2_documents_bucket_company_scoped.sql.
 * These are the only folders the public marketplace renders unauthenticated,
 * so they're the only ones the `media` bucket (public=true) is allowed to
 * hold going forward — everything else lives in the private `documents`
 * bucket. See `isPrivateFolder` below and the S3-invariant test in
 * storage.test.ts (OWNER DIRECTIVE: sensitive documents must NEVER route to
 * S3/CDN — S3_IMAGE_FOLDERS must stay a subset of this set).
 */
export const PUBLIC_MEDIA_FOLDERS: ReadonlySet<StorageFolder> = new Set<StorageFolder>([
  "properties",
  "units",
  "listings",
  "projects",
  "companies",
  "content-pages",
  "profiles",
]);

/** True for any folder NOT on the public-imagery allow-list — these route to
 * the private `documents` bucket and require signed URLs to read. */
export function isPrivateFolder(folder: StorageFolder): boolean {
  return !PUBLIC_MEDIA_FOLDERS.has(folder);
}

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
export const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime"];
export const MAX_VIDEO_FILE_SIZE = 200 * 1024 * 1024; // 200 MB per file

/**
 * Checks if a file is a valid video by MIME type (video/mp4 or video/quicktime).
 */
export function isVideoFile(file: File): boolean {
  return ALLOWED_VIDEO_TYPES.includes((file.type || "").trim().toLowerCase());
}

/**
 * Checks if a URL points to a video file by extension (mp4 or mov).
 * Matches ALLOWED_VIDEO_TYPES — extend both when adding new video formats.
 */
export function isVideoUrl(url: string): boolean {
  // Tolerates a query string or fragment after the extension (e.g. the
  // #t=0.001 first-frame fragment the video tiles append).
  return /\.(mp4|mov)([?#].*)?$/i.test(url);
}

/**
 * `src` for a video thumbnail tile: appends the #t=0.001 media fragment so
 * the browser decodes the first frame as a poster. Phone-recorded MP4s keep
 * the moov atom at the END of the file, so preload="metadata" alone fetches
 * no frame data and the tile renders blank. No-op when the URL already
 * carries a fragment (a second `#` would be malformed).
 */
export function videoTileSrc(url: string): string {
  return url.includes("#") ? url : `${url}#t=0.001`;
}

const ALLOWED_DOCUMENT_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

/**
 * Checks if a file is a valid image by MIME type or extension.
 * Used for filtering files for compression (non-throwing).
 */
function isImageFile(file: File): boolean {
  let mimeType = (file.type || "").trim().toLowerCase();

  // Normalize non-standard but common MIME types
  if (mimeType === "image/jpg") {
    mimeType = "image/jpeg";
  }

  // Check normalized MIME type
  if (ALLOWED_IMAGE_TYPES.includes(mimeType)) {
    return true;
  }

  // Fallback to extension check
  if (!mimeType || mimeType === "") {
    const extension = file.name.split(".").pop()?.toLowerCase();
    const validExtensions = ["jpg", "jpeg", "png", "webp", "gif"];
    return !!(extension && validExtensions.includes(extension));
  }

  return false;
}

/**
 * Detects HEIC/HEIF (iPhone default photo format) by MIME type or file
 * extension, so INVALID_MIME rejections can point the user at the fix
 * (switch camera format, or convert) instead of a generic "not supported"
 * message. Not a decoder — purely a UX classification of the reject reason.
 */
function isHeicFile(fileType: string | undefined, fileName: string): boolean {
  const type = (fileType ?? "").trim().toLowerCase();
  if (type.includes("heic") || type.includes("heif")) return true;
  const extension = fileName.split(".").pop()?.toLowerCase();
  return extension === "heic" || extension === "heif";
}

/**
 * Converts a single HEIC/HEIF file (the default photo format on iPhone) to
 * JPEG via heic2any, renaming it to `<basename>.jpg` so it flows through the
 * existing validate/compress/upload pipeline unchanged. Non-HEIC files are
 * returned untouched (no dynamic import triggered).
 *
 * On decode failure (corrupt file, wasm load failure) throws the same
 * StorageError("INVALID_MIME") a rejected MIME type would — this keeps the
 * failure in the existing Sentry-quiet "expected user error" class at call
 * sites (see isExpectedUploadError-style checks in image-upload.tsx etc.)
 * and routes to getStorageErrorMessage's HEIC-specific copy below.
 */
async function convertHeicIfNeeded(file: File): Promise<File> {
  if (!isHeicFile(file.type, file.name)) return file;

  try {
    const { default: heic2any } = await loadHeicConversionModule();
    const output = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
    // Multi-frame HEIC (e.g. an iPhone Live Photo bundles a still + a short
    // motion clip) resolves as a Blob[] — take the first (still) frame only.
    // Correct for a property-photo upload context: we want a JPEG, not the
    // motion component.
    const blob = Array.isArray(output) ? output[0] : output;
    const baseName = file.name.replace(/\.(heic|heif)$/i, "") || "photo";
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
  } catch (err) {
    throw new StorageError("INVALID_MIME", {
      fileName: file.name,
      fileType: file.type || undefined,
      raw: err instanceof Error ? err.message : String(err),
    });
  }
}

/**
 * Bounded-concurrency HEIC→JPEG conversion pass for a batch of files.
 * Non-HEIC files resolve immediately (convertHeicIfNeeded is a same-tick
 * no-op for them) so mixing HEIC and non-HEIC files in one drop is cheap.
 * Skips the compression-module import entirely when the batch has no HEIC
 * files at all.
 */
async function convertHeicBatch(files: File[]): Promise<File[]> {
  if (!files.some((f) => isHeicFile(f.type, f.name))) return files;
  const { compressBatch } = await loadCompressionModule();
  return compressBatch(files, convertHeicIfNeeded, HEIC_CONVERSION_CONCURRENCY);
}

/**
 * Normalizes and validates image MIME types with fallback to extension-based validation.
 * Handles browser inconsistencies:
 * - Normalizes "image/jpg" to "image/jpeg"
 * - Case-insensitive matching
 * - Falls back to extension validation when MIME type is empty
 *
 * @throws StorageError with code INVALID_MIME if validation fails
 */
function normalizeAndValidateImageMime(file: File): void {
  if (!isImageFile(file)) {
    throw new StorageError("INVALID_MIME", {
      fileName: file.name,
      fileType: file.type || undefined,
    });
  }
}

/**
 * `accept` strings for `<input type="file">` so the OS file picker only
 * shows file types the storage layer actually permits. Both MIME types and
 * extensions are listed for cross-browser/OS reliability — some platforms
 * (notably mobile Safari) are stricter about MIME-type-only filters.
 *
 * Single source of truth — every upload UI in the app should read from here
 * instead of hard-coding `image/*` (which is too broad and lets the user
 * pick TIFF/BMP/HEIC files that the server then rejects).
 */
export const IMAGE_ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif";

export const DOCUMENT_ACCEPT =
  "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,.doc,.docx";

export const IMAGE_OR_PDF_ACCEPT = `${IMAGE_ACCEPT},application/pdf,.pdf`;

export const IMAGE_OR_DOCUMENT_ACCEPT = `${IMAGE_ACCEPT},${DOCUMENT_ACCEPT}`;

/** Accept string for `<input type="file">` — video only (MP4 + MOV). */
export const VIDEO_ACCEPT = "video/mp4,video/quicktime,.mp4,.mov";
/** Images + videos (for property/unit listing media galleries). */
export const IMAGE_OR_VIDEO_ACCEPT = `${IMAGE_ACCEPT},${VIDEO_ACCEPT}`;
/** Images + videos + PDFs (for property listing media that already had PDF support). */
export const IMAGE_VIDEO_OR_PDF_ACCEPT = `${IMAGE_ACCEPT},${VIDEO_ACCEPT},application/pdf,.pdf`;

/**
 * Stable error codes thrown by this module. The UI layer maps these to
 * localized messages; this layer stays agnostic to language and presentation.
 *
 *   FILE_TOO_LARGE    — size exceeds MAX_FILE_SIZE or server payload limit
 *   INVALID_MIME      — file's MIME type not in the allow-list
 *   DUPLICATE         — a file with the same path already exists
 *   INVALID_PATH      — entityId contained path separators
 *   UPLOAD_FAILED     — catch-all for unexpected backend errors during upload
 *                       (including the post-upload signing step)
 *   SIGN_FAILED       — READ-time signed-URL creation failed (resolveDocumentUrl)
 *   DELETE_FAILED     — catch-all for backend delete errors
 */
export type StorageErrorCode =
  | "FILE_TOO_LARGE"
  | "INVALID_MIME"
  | "DUPLICATE"
  | "INVALID_PATH"
  | "UPLOAD_FAILED"
  | "SIGN_FAILED"
  | "DELETE_FAILED";

export interface StorageErrorDetails {
  fileName?: string;
  fileType?: string;
  sizeBytes?: number;
  limitBytes?: number;
  /** Raw error message from the underlying SDK, useful for logs / dev console. */
  raw?: string;
}

export class StorageError extends Error {
  readonly code: StorageErrorCode;
  readonly details: StorageErrorDetails;

  constructor(code: StorageErrorCode, details: StorageErrorDetails = {}, fallbackMessage?: string) {
    super(fallbackMessage ?? code);
    this.name = "StorageError";
    this.code = code;
    this.details = details;
  }
}

/**
 * True for StorageErrors that represent an expected user-input validation
 * outcome (wrong file type, oversized file, duplicate name) rather than a
 * genuine application/infrastructure failure. Upload UIs use this to skip
 * `console.error` for these — Sentry's captureConsoleIntegration forwards
 * every console.error, so logging expected user mistakes spams Sentry with
 * non-actionable noise (#3080). Real failures (RLS denials, network errors,
 * INVALID_MIME/FILE_TOO_LARGE/DUPLICATE surfaced from a genuine server-side
 * rejection at the raw Supabase-upload call site) are unaffected — this
 * only gates the redundant component-level log, not storage.ts's own.
 */
export function isExpectedUploadError(error: unknown): boolean {
  return (
    error instanceof StorageError &&
    (error.code === "INVALID_MIME" ||
      error.code === "FILE_TOO_LARGE" ||
      error.code === "DUPLICATE")
  );
}

/**
 * Classify a raw Supabase Storage error message into one of our stable
 * StorageErrorCode values. Exported for direct unit testing — the regex
 * patterns are the only contract this layer has with Supabase's error
 * surface and they need to stay stable across SDK version bumps.
 */
export function classifySupabaseError(raw: string): StorageErrorCode {
  // 413 Payload Too Large / storage quota signals — covers server-side size
  // rejection that slips past the client-side guard (#1415)
  if (/payload too large|exceeded the maximum|file size|too large|413/i.test(raw)) return "FILE_TOO_LARGE";
  // 416 Range Not Satisfiable — bubble as UPLOAD_FAILED with the raw message
  // so the UI can surface an actionable description to the user (#1379)
  if (/range not satisfiable|416/i.test(raw)) return "UPLOAD_FAILED";
  if (/mime type|not allowed/i.test(raw)) return "INVALID_MIME";
  if (/duplicate|already exists/i.test(raw)) return "DUPLICATE";
  return "UPLOAD_FAILED";
}

/**
 * Upload a single file to Supabase Storage. Throws StorageError on failure.
 *
 * `companyId` is REQUIRED (multi-tenant isolation — #3059 C1 phase 2): every
 * path is prefixed `{companyId}/{folder}/…` so the storage.objects RLS
 * policies on both `media` and `documents` can scope INSERT/SELECT to the
 * caller's company. Never pass a placeholder — gate the upload UI on
 * `!!companyId` instead (see the `enabled: !!companyId` React Query
 * convention in AGENTS.md).
 *
 * Routing: PUBLIC folders (see {@link PUBLIC_MEDIA_FOLDERS}) upload to the
 * `media` bucket and return a permanent public URL. PRIVATE folders upload
 * to the `documents` bucket and return a 10-minute signed URL — for those,
 * PERSIST `result.path`, never `result.url` (it expires). Use
 * {@link resolveDocumentUrl} to re-derive a fresh signed URL from a stored
 * path at render time.
 */
export async function uploadFile(
  file: File,
  folder: StorageFolder,
  companyId: string,
  entityId?: string,
  sizeLimit: number = MAX_FILE_SIZE,
): Promise<UploadResult> {
  // Validate file size client-side
  if (file.size > sizeLimit) {
    throw new StorageError(
      "FILE_TOO_LARGE",
      { fileName: file.name, sizeBytes: file.size, limitBytes: sizeLimit },
    );
  }

  // Validate companyId/entityId to prevent path traversal — both become path
  // segments, so either could smuggle a `../` or an extra `/` if a caller
  // (a bug, not a user) ever passed something other than the real UUID.
  if (!companyId || companyId.includes("/") || companyId.includes("\\")) {
    console.error("Invalid companyId detected:", companyId);
    throw new StorageError("INVALID_PATH", { raw: companyId });
  }
  if (entityId && (entityId.includes("/") || entityId.includes("\\"))) {
    console.error("Invalid entityId detected:", entityId);
    throw new StorageError("INVALID_PATH", { raw: entityId });
  }

  // Create unique file path: {companyId}/{folder}/{entityId?}/{ts}_{rand}.{ext}
  const fileExt = file.name.split(".").pop();
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(7);
  const fileName = entityId
    ? `${entityId}/${timestamp}_${randomId}.${fileExt}`
    : `${timestamp}_${randomId}.${fileExt}`;
  const filePath = `${companyId}/${folder}/${fileName}`;

  const bucket = isPrivateFolder(folder) ? DOCUMENTS_BUCKET : MEDIA_BUCKET;

  // Upload to Supabase Storage
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(filePath, file, {
      cacheControl: "3600",
      upsert: false,
      // AC5 — store the correct MIME type (mirrors ContractDocumentsTab). Without
      // it Supabase infers a generic/wrong type and downstream view/verify break.
      // Fall back to a safe binary type when the browser reports no file.type.
      contentType: file.type || "application/octet-stream",
    });

  if (error) {
    console.error("Upload error:", error);
    const raw = error.message || "";
    throw new StorageError(
      classifySupabaseError(raw),
      {
        fileName: file.name,
        fileType: file.type || undefined,
        sizeBytes: file.size,
        limitBytes: sizeLimit,
        raw,
      },
      raw,
    );
  }

  if (isPrivateFolder(folder)) {
    // Private class — the bucket is not public, so a signed URL is required
    // to render/download. Callers MUST persist `path`, not this URL.
    const { data: signed, error: signError } = await supabase.storage
      .from(DOCUMENTS_BUCKET)
      .createSignedUrl(filePath, PRIVATE_URL_TTL_SECONDS);
    if (signError || !signed) {
      const raw = signError?.message || "Failed to create signed URL";
      throw new StorageError("UPLOAD_FAILED", { fileName: file.name, raw }, raw);
    }
    return {
      url: signed.signedUrl,
      path: filePath,
    };
  }

  // Public class — permanent public URL, safe to persist directly.
  const { data: urlData } = supabase.storage
    .from(MEDIA_BUCKET)
    .getPublicUrl(filePath);

  return {
    url: urlData.publicUrl,
    path: filePath,
  };
}

/**
 * Upload a mixed batch of files (typically images + PDFs) to Supabase
 * Storage. Image-typed files in the batch are compressed + watermarked
 * (when the folder is on the watermark allowlist) just like
 * `uploadImages` does — non-image files (PDFs, docs) are passed through
 * untouched. This matters for the `<ImageUpload accept={IMAGE_OR_PDF}>`
 * path which routes through here; without per-file compression, images
 * uploaded via that path would skip the size/watermark step entirely.
 *
 * Pass `compression: false` to disable image processing for callers
 * (e.g. `uploadImages` already pre-compresses before delegating here).
 */
export async function uploadFiles(
  files: File[],
  folder: StorageFolder,
  companyId: string,
  entityId?: string,
  options?: ImageUploadOptions,
): Promise<UploadResult[]> {
  // Convert any HEIC/HEIF files to JPEG up front (before the video-MIME
  // precheck and the image-filter below) so mixed image+PDF/video batches
  // routed here directly (uploadFilesGated's non-S3 branch) get the same
  // HEIC support as uploadImages. No-ops instantly when the caller already
  // converted (uploadImages delegates here with compression: false).
  const converted = await convertHeicBatch(files);

  // Pre-validate video MIME types before any compression/upload
  for (const file of converted) {
    if (file.type.startsWith("video/") && !isVideoFile(file)) {
      throw new StorageError("INVALID_MIME", {
        fileName: file.name,
        fileType: file.type || undefined,
      });
    }
  }

  const compressOpts = await resolveCompressOptions(folder, options);
  const processed = await compressImagesInBatch(converted, compressOpts);

  const results: UploadResult[] = [];
  for (const file of processed) {
    const result = await uploadFile(
      file,
      folder,
      companyId,
      entityId,
      isVideoFile(file) ? MAX_VIDEO_FILE_SIZE : MAX_FILE_SIZE,
    );
    results.push(result);
  }

  return results;
}

/** Pass `compression: false` to skip resize + watermark entirely. */
export type ImageUploadOptions = {
  compression?: false | CompressOptions;
};

async function resolveCompressOptions(
  folder: StorageFolder,
  options?: ImageUploadOptions,
): Promise<CompressOptions | null> {
  if (options?.compression === false) return null;
  const { shouldWatermarkFolder } = await loadCompressionModule();
  const overrides = options?.compression ?? {};
  return {
    watermark: overrides.watermark ?? shouldWatermarkFolder(folder),
    ...overrides,
  };
}

/**
 * Compress + watermark the image-typed files of a (possibly mixed) batch with
 * bounded concurrency, passing videos/PDFs/docs through unchanged. No-op when
 * compression is disabled (compressOpts === null). Shared by the Supabase
 * batch path (uploadFiles) and the S3-gated branches — the Nest property-media
 * endpoint stores buffers as-is, so ALL image processing happens client-side
 * regardless of upload destination.
 */
async function compressImagesInBatch(
  files: File[],
  compressOpts: CompressOptions | null,
): Promise<File[]> {
  if (!compressOpts) return files;
  const imagesInBatch = files.filter((f) => isImageFile(f));
  if (imagesInBatch.length === 0) return files;
  const { compressImage, compressBatch } = await loadCompressionModule();
  const compressedImages = await compressBatch(imagesInBatch, (f) =>
    compressImage(f, compressOpts),
  );
  const compressedByOriginal = new Map(
    imagesInBatch.map((orig, i) => [orig, compressedImages[i]]),
  );
  return files.map((f) => compressedByOriginal.get(f) ?? f);
}

/**
 * Upload an image file with validation. Throws StorageError on failure.
 * Compresses + watermarks through compressImage() unless disabled.
 */
export async function uploadImage(
  file: File,
  folder: StorageFolder,
  companyId: string,
  entityId?: string,
  options?: ImageUploadOptions,
): Promise<UploadResult> {
  const converted = await convertHeicIfNeeded(file);
  normalizeAndValidateImageMime(converted);

  const compressOpts = await resolveCompressOptions(folder, options);
  let processed = converted;
  if (compressOpts) {
    const { compressImage } = await loadCompressionModule();
    processed = await compressImage(converted, compressOpts);
  }

  return uploadFile(processed, folder, companyId, entityId);
}

/**
 * Upload multiple images with validation. Throws StorageError on the first
 * failure (caller should expect the batch to be aborted; partial successes
 * are not rolled back). Compression runs with bounded concurrency
 * (compressBatch) so a 20-photo batch on a 4-core mobile device doesn't
 * thrash by spawning 20 simultaneous workers.
 */
export async function uploadImages(
  files: File[],
  folder: StorageFolder,
  companyId: string,
  entityId?: string,
  options?: ImageUploadOptions,
): Promise<UploadResult[]> {
  const converted = await convertHeicBatch(files);
  for (const file of converted) {
    normalizeAndValidateImageMime(file);
  }

  const compressOpts = await resolveCompressOptions(folder, options);
  let processed: File[] = converted;
  if (compressOpts) {
    const { compressImage, compressBatch } = await loadCompressionModule();
    processed = await compressBatch(converted, (f) => compressImage(f, compressOpts));
  }

  // Skip uploadFiles' own compression pass — we already processed. Its HEIC
  // conversion pass also no-ops here for the same reason: `converted` is
  // already image/jpeg, so uploadFiles' own convertHeicBatch short-circuits
  // via `!files.some(isHeicFile)` without re-invoking heic2any.
  return uploadFiles(processed, folder, companyId, entityId, { compression: false });
}

/**
 * Upload a single video file (MP4 or MOV only). Validates MIME type and enforces
 * MAX_VIDEO_FILE_SIZE (200 MB). Throws StorageError on failure.
 */
export async function uploadVideo(
  file: File,
  folder: StorageFolder,
  companyId: string,
  entityId?: string,
): Promise<UploadResult> {
  if (!isVideoFile(file)) {
    throw new StorageError("INVALID_MIME", {
      fileName: file.name,
      fileType: file.type || undefined,
    });
  }
  return uploadFile(file, folder, companyId, entityId, MAX_VIDEO_FILE_SIZE);
}

/**
 * Upload a document file with validation
 */
export async function uploadDocument(
  file: File,
  folder: StorageFolder,
  companyId: string,
  entityId?: string,
): Promise<UploadResult> {
  const allAllowedTypes = [...ALLOWED_IMAGE_TYPES, ...ALLOWED_DOCUMENT_TYPES];

  if (!allAllowedTypes.includes(file.type)) {
    throw new StorageError("INVALID_MIME", {
      fileName: file.name,
      fileType: file.type || undefined,
    });
  }

  return uploadFile(file, folder, companyId, entityId);
}

/**
 * Delete a file from the `media` bucket. Throws StorageError on failure.
 * Accepts either a storage path or a public URL (will extract path automatically)
 *
 * NOT CALLED ANYWHERE in the app today (verified during #3035 C1 phase 1 —
 * dead code, zero importers). WARNING: since
 * 20260705160000_lock_media_bucket_writes_to_service_role.sql, the `media`
 * bucket's client DELETE policy only allows new-shape (company-scoped)
 * public-imagery paths (#3059 C1 phase 2) — this function WILL fail with a
 * storage permission error against any other path (legacy-shape media
 * objects, or anything in the private `documents` bucket, which has NO
 * client DELETE policy at all — service_role only). New delete call sites
 * for private documents should go through a guarded Nest endpoint instead
 * (see DocumentsService.deleteContractDocument / deleteContractDocumentViaApi
 * in src/services/api.ts for the pattern: verify entity ownership
 * server-side, then delete via the service-role Supabase client). This
 * helper only ever targets `media` — it is never valid for `documents`.
 */
export async function deleteFile(filePathOrUrl: string): Promise<void> {
  const filePath = extractStoragePath(filePathOrUrl);

  const { error } = await supabase.storage
    .from(MEDIA_BUCKET)
    .remove([filePath]);

  if (error) {
    console.error("Delete error:", error);
    throw new StorageError("DELETE_FAILED", { raw: error.message }, error.message);
  }
}

/**
 * Delete multiple files from the `media` bucket. Throws StorageError on failure.
 * Accepts either storage paths or public URLs (will extract paths automatically)
 *
 * NOT CALLED ANYWHERE in the app today (same dead-code / service_role-only
 * caveat as {@link deleteFile} above — see that doc comment, #3035 C1 phase 1,
 * #3059 C1 phase 2). Never valid for the private `documents` bucket.
 */
export async function deleteFiles(filePathsOrUrls: string[]): Promise<void> {
  if (filePathsOrUrls.length === 0) return;

  const filePaths = filePathsOrUrls.map(path => extractStoragePath(path));

  const { error } = await supabase.storage
    .from(MEDIA_BUCKET)
    .remove(filePaths);

  if (error) {
    console.error("Delete error:", error);
    throw new StorageError("DELETE_FAILED", { raw: error.message }, error.message);
  }
}

/**
 * Get the public URL for a `media`-bucket storage path (public-imagery class
 * only — see {@link PUBLIC_MEDIA_FOLDERS}). Never valid for a private
 * `documents` path; use {@link resolveDocumentUrl} for those.
 */
export function getPublicUrl(filePath: string): string {
  const { data } = supabase.storage
    .from(MEDIA_BUCKET)
    .getPublicUrl(filePath);

  return data.publicUrl;
}

/**
 * Extract storage path from a Supabase public/signed URL or legacy Django
 * media path. Handles:
 * - Public URLs: https://domain.com/storage/v1/object/public/media/path/to/file.jpg
 * - Signed URLs: https://domain.com/storage/v1/object/sign/documents/path/to/file.pdf?token=…
 *   (the `documents` bucket's reads always come back as signed URLs — see
 *   {@link resolveDocumentUrl})
 * - Legacy paths: /dallali-staging/media/path/to/file.jpg
 * - Already clean paths: path/to/file.jpg
 */
export function extractStoragePath(urlOrPath: string): string {
  let extracted: string;

  if (!urlOrPath.startsWith('/') && !urlOrPath.startsWith('http')) {
    extracted = urlOrPath;
  } else {
    // Match a signed-URL pattern first — /storage/v1/object/sign/{bucket}/{path}?token=…
    // (documents-bucket reads). The path portion stops at the query string.
    const signedMatch = urlOrPath.match(/\/storage\/v1\/object\/sign\/[^/]+\/([^?]+)/);
    if (signedMatch) {
      extracted = decodeURIComponent(signedMatch[1]);
    } else {
      // Match Supabase public storage URL pattern
      const supabaseMatch = urlOrPath.match(/\/storage\/v1\/object\/public\/[^/]+\/(.+)$/);
      if (supabaseMatch) {
        extracted = supabaseMatch[1];
      } else {
        // Match /media/PATH pattern (legacy Django URLs)
        const mediaMatch = urlOrPath.match(/\/media\/(.+)$/);
        if (mediaMatch) {
          extracted = mediaMatch[1];
        } else if (urlOrPath.includes('://')) {
          // If URL contains domain, extract path after either bucket name
          const mediaParts = urlOrPath.split(`/${MEDIA_BUCKET}/`);
          const documentsParts = urlOrPath.split(`/${DOCUMENTS_BUCKET}/`);
          if (mediaParts.length > 1) {
            extracted = mediaParts[mediaParts.length - 1];
          } else if (documentsParts.length > 1) {
            extracted = documentsParts[documentsParts.length - 1];
          } else {
            extracted = urlOrPath;
          }
        } else {
          extracted = urlOrPath;
        }
      }
    }
  }

  // Validate extracted path to prevent path traversal
  if (extracted.includes('..') || extracted.startsWith('/')) {
    console.error("Invalid storage path detected:", extracted);
    throw new StorageError("INVALID_PATH", { raw: extracted });
  }

  return extracted;
}

/**
 * Single read helper for the PRIVATE document class (#3059 C1 phase 2). Turns
 * a stored value into a URL safe to pass to `<img src>` / `<a href>` /
 * `window.open`:
 *
 * - A legacy full http(s) URL (pre-migration rows persisted the old `media`
 *   public URL) is returned as-is — those objects still resolve directly
 *   until the phase-2 backfill moves them into `documents`.
 * - Anything else is treated as a bare `documents`-bucket path and signed
 *   fresh via `createSignedUrl` (10-minute default TTL, matching the
 *   `payment-proofs` precedent in PaymentVerifications.tsx).
 *
 * Always call this at render/download time — never cache the signed URL
 * past its TTL, and never persist it back into the DB (persist `path`).
 */
export async function resolveDocumentUrl(
  pathOrUrl: string,
  opts?: { ttlSeconds?: number },
): Promise<string> {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }

  const ttlSeconds = opts?.ttlSeconds ?? PRIVATE_URL_TTL_SECONDS;
  const { data, error } = await supabase.storage
    .from(DOCUMENTS_BUCKET)
    .createSignedUrl(pathOrUrl, ttlSeconds);

  if (error || !data) {
    const raw = error?.message || "Failed to create signed URL";
    // SIGN_FAILED (not UPLOAD_FAILED): this is a READ-time failure — callers
    // branching on the code must not misclassify it as a failed upload.
    throw new StorageError("SIGN_FAILED", { raw }, raw);
  }

  return data.signedUrl;
}

/**
 * Converts a value coming OUT of an edit-flow's `<ImageUpload>` state back
 * into what should be PERSISTED for the private-document class (#3059 C1
 * phase 2). Use this (not bare {@link extractStoragePath}) whenever a
 * component may hold a MIX of freshly-uploaded/hydrated signed URLs and
 * untouched legacy full `media` URLs in the same array — e.g. an edit dialog
 * that re-opens existing `photos`/`images` values into `<ImageUpload>` via
 * {@link resolveDocumentUrl} for preview (see EditableViewReportDialog,
 * InspectionReport).
 *
 * Only a SIGNED url (`/storage/v1/object/sign/...` — either uploaded this
 * session, or freshly re-signed from a hydrated stored path) is converted
 * back to its bare storage path via `extractStoragePath`. Everything else
 * (a legacy full public `media` URL, or an already-bare path) passes through
 * UNCHANGED: `extractStoragePath` would happily strip a `/public/{bucket}/`
 * URL down to a bucket-relative path too, but that path refers to an object
 * in the PUBLIC `media` bucket — persisting it as if it were a `documents`
 * path would silently corrupt the reference (the next {@link
 * resolveDocumentUrl} call would try, and fail, to sign it against the
 * wrong bucket).
 *
 * Create-only flows (no hydration of existing values, e.g. AddTaskNoteDialog)
 * don't need this — every value there is guaranteed to be a fresh signed URL,
 * so calling `extractStoragePath` directly is safe.
 */
export function toPersistableStorageValue(urlOrPath: string): string {
  return /\/storage\/v1\/object\/sign\//.test(urlOrPath)
    ? extractStoragePath(urlOrPath)
    : urlOrPath;
}

// ─── S3-gated upload helpers (flags: USE_S3_PROPERTY_IMAGES / _VIDEOS) ───────
//
// SCOPE: ONLY "properties" and "units" folders are eligible for S3 rerouting.
// All other folders (contracts, licenses, deeds, receipts, maintenance, tasks,
// inspections, etc.) always use the Supabase Storage path regardless of the flag.
// Callers in image-upload.tsx and image-gallery-modal.tsx call these wrappers
// instead of calling uploadImage / uploadImages directly.

// Exported (read-only) so storage.test.ts can assert the OWNER DIRECTIVE
// below against the REAL set, not a hand-maintained mirror that could drift.
export const S3_IMAGE_FOLDERS: ReadonlySet<StorageFolder> = new Set<StorageFolder>(['properties', 'units']);

// OWNER DIRECTIVE (#3059 C1 phase 2): sensitive documents must NEVER route to
// S3/CDN. S3_IMAGE_FOLDERS is asserted (at module load, and again in
// storage.test.ts) to be a subset of PUBLIC_MEDIA_FOLDERS — if a private
// (documents-class) folder were ever added to the S3 gate, PII would be
// pushed to a public CDN. Fail loudly at import time rather than silently.
for (const folder of S3_IMAGE_FOLDERS) {
  if (!PUBLIC_MEDIA_FOLDERS.has(folder)) {
    throw new Error(
      `S3_IMAGE_FOLDERS must stay a subset of PUBLIC_MEDIA_FOLDERS (owner directive, #3059) — ` +
        `"${folder}" is a private/document folder and must never route to S3/CDN.`,
    );
  }
}

/**
 * Throw FILE_TOO_LARGE before ANY upload starts if a video in the batch
 * exceeds the cap — an oversize rejection mid-batch would orphan the
 * already-uploaded S3 objects (the caller never sees their URLs, so it can
 * never delete them). Also fails fast client-side before streaming 200+ MB
 * the endpoint would reject anyway.
 */
function assertVideoSizesWithinLimit(files: File[]): void {
  for (const file of files) {
    if (isVideoFile(file) && file.size > MAX_VIDEO_FILE_SIZE) {
      throw new StorageError("FILE_TOO_LARGE", {
        fileName: file.name,
        sizeBytes: file.size,
        limitBytes: MAX_VIDEO_FILE_SIZE,
      });
    }
  }
}

/**
 * Map a (plural) storage folder to the backend's (singular) `entity_type`.
 * The S3 gate only admits "properties"/"units", and the property-media
 * endpoint (#2863) validates `entity_type ∈ { "property", "unit" }` — so a
 * bare `folder as PropertyMediaEntityType` cast would send the wrong (plural)
 * value and the server would reject every upload.
 */
function folderToEntityType(folder: StorageFolder): PropertyMediaEntityType {
  if (folder === 'properties') return 'property';
  if (folder === 'units') return 'unit';
  // Loud failure: if S3_IMAGE_FOLDERS is ever expanded without adding a case
  // here, a silent default would send an invalid entity_type and 400 every
  // upload for the new folder.
  throw new Error(`folderToEntityType: no entity_type mapping for folder "${folder}"`);
}

/**
 * Upload a single image for a property or unit, routing to S3 when
 * USE_S3_PROPERTY_IMAGES is enabled and the folder is "properties" or "units".
 * All other folders always use the existing Supabase Storage path.
 */
export async function uploadImageGated(
  file: File,
  folder: StorageFolder,
  companyId: string,
  entityId?: string,
  options?: ImageUploadOptions,
): Promise<UploadResult> {
  if (USE_S3_PROPERTY_IMAGES && S3_IMAGE_FOLDERS.has(folder)) {
    const converted = await convertHeicIfNeeded(file);
    // Match uploadImagesGated's pre-validation so the single-image S3 path
    // rejects a bad MIME type before hitting the network.
    normalizeAndValidateImageMime(converted);
    // Same client-side compression + watermark pipeline as the Supabase path —
    // the Nest endpoint stores the buffer as-is.
    const compressOpts = await resolveCompressOptions(folder, options);
    let processed = converted;
    if (compressOpts) {
      const { compressImage } = await loadCompressionModule();
      processed = await compressImage(converted, compressOpts);
    }
    const entityType = folderToEntityType(folder);
    const result = await uploadPropertyImageViaApi(processed, entityType, entityId);
    return { url: result.url, path: result.key };
  }
  return uploadImage(file, folder, companyId, entityId, options);
}

/**
 * Upload multiple images for a property or unit, routing to S3 when
 * USE_S3_PROPERTY_IMAGES is enabled and the folder is "properties" or "units".
 * All other folders always use the existing Supabase Storage path.
 *
 * When routing to S3 the images run the same client-side compression +
 * watermark pipeline as the Supabase path, then upload sequentially.
 */
export async function uploadImagesGated(
  files: File[],
  folder: StorageFolder,
  companyId: string,
  entityId?: string,
  options?: ImageUploadOptions,
): Promise<UploadResult[]> {
  if (USE_S3_PROPERTY_IMAGES && S3_IMAGE_FOLDERS.has(folder)) {
    const converted = await convertHeicBatch(files);
    // Pre-validate the entire batch before starting any upload — matches the
    // fail-fast behavior of uploadImages() so no partial S3 uploads occur.
    for (const file of converted) {
      normalizeAndValidateImageMime(file);
    }
    const compressOpts = await resolveCompressOptions(folder, options);
    const processed = await compressImagesInBatch(converted, compressOpts);
    const entityType = folderToEntityType(folder);
    const results: UploadResult[] = [];
    for (const file of processed) {
      const result = await uploadPropertyImageViaApi(file, entityType, entityId);
      results.push({ url: result.url, path: result.key });
    }
    return results;
  }
  return uploadImages(files, folder, companyId, entityId, options);
}

/**
 * Upload a mixed batch of files (images, videos, PDFs) for a property or unit.
 * Routing (folder must be "properties" or "units"; all other folders always
 * use Supabase Storage):
 *   - USE_S3_PROPERTY_IMAGES: images AND videos route to the NestJS
 *     property-media S3 endpoint (images are compressed + watermarked
 *     client-side first); PDFs/docs stay on Supabase Storage.
 *   - USE_S3_PROPERTY_VIDEOS (images flag off): ONLY videos route to S3;
 *     images keep the Supabase path with the same client-side pipeline.
 *
 * This replaces the `uploadFiles` call in gallery-modal and image-upload for the
 * video/mixed path, ensuring videos land in S3 (via the NestJS property-media
 * endpoint) rather than Supabase Storage.
 */
export async function uploadFilesGated(
  files: File[],
  folder: StorageFolder,
  companyId: string,
  entityId?: string,
  options?: ImageUploadOptions,
): Promise<UploadResult[]> {
  if (USE_S3_PROPERTY_IMAGES && S3_IMAGE_FOLDERS.has(folder)) {
    assertVideoSizesWithinLimit(files);
    // Convert HEIC files first — post-conversion they satisfy isImageFile()
    // and route to S3 instead of falling through to the Supabase "other
    // files" branch below with a still-HEIC content type.
    const converted = await convertHeicBatch(files);
    // Images get the same client-side compression + watermark pipeline as the
    // Supabase path; videos/PDFs pass through the helper unchanged.
    const compressOpts = await resolveCompressOptions(folder, options);
    const processed = await compressImagesInBatch(converted, compressOpts);
    const entityType = folderToEntityType(folder);
    const results: UploadResult[] = [];
    for (const file of processed) {
      if (isImageFile(file) || isVideoFile(file)) {
        const r = await uploadPropertyImageViaApi(file, entityType, entityId);
        results.push({ url: r.url, path: r.key });
      } else {
        // PDFs and other non-image/video files still go to Supabase Storage.
        const r = await uploadFile(file, folder, companyId, entityId, MAX_FILE_SIZE);
        results.push(r);
      }
    }
    return results;
  }

  // Video-only S3 routing (USE_S3_PROPERTY_VIDEOS): videos go to the NestJS
  // property-media endpoint, while images in the same batch keep the Supabase
  // path with its client-side compression + watermark pipeline. Videos have no
  // Supabase fallback — the `media` bucket rejects video MIME types and caps
  // files at 10 MB — so this branch is the only working path for them.
  if (USE_S3_PROPERTY_VIDEOS && S3_IMAGE_FOLDERS.has(folder) && files.some(isVideoFile)) {
    assertVideoSizesWithinLimit(files);
    const entityType = folderToEntityType(folder);

    const results: (UploadResult | undefined)[] = new Array(files.length);
    const rest: File[] = [];
    const restIndices: number[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (isVideoFile(file)) {
        const r = await uploadPropertyImageViaApi(file, entityType, entityId);
        results[i] = { url: r.url, path: r.key };
      } else {
        rest.push(file);
        restIndices.push(i);
      }
    }

    if (rest.length > 0) {
      const restResults = await uploadFiles(rest, folder, companyId, entityId, options);
      restIndices.forEach((originalIndex, j) => {
        results[originalIndex] = restResults[j];
      });
    }
    return results as UploadResult[];
  }

  return uploadFiles(files, folder, companyId, entityId, options);
}

/**
 * True when a URL was uploaded through the NestJS property-media module and
 * therefore lives in S3/CDN.  All such URLs contain the `/property-media/`
 * purpose-prefix that ScopedStorage adds at upload time (see
 * property-media.service.ts `buildCdnUrl`).  Using a positive assertion avoids
 * misclassifying external URLs, legacy Django media URLs, or typos as S3
 * targets (which would trigger a spurious DELETE call to the NestJS endpoint).
 */
function isS3CdnUrl(url: string): boolean {
  return url.includes('/property-media/');
}

/**
 * Delete multiple media files for a property or unit, routing S3 CDN URLs
 * through the NestJS DELETE /property-media/images endpoint when either
 * USE_S3_PROPERTY_IMAGES or USE_S3_PROPERTY_VIDEOS is enabled (either flag
 * means S3/CDN URLs can exist for these folders). Supabase Storage URLs are
 * always deleted via the Supabase client regardless of the flags.
 *
 * Unchanged by #3059 C1 phase 2 — `folder` here is always "properties" or
 * "units" (S3_IMAGE_FOLDERS), which are public-imagery (media-bucket) only.
 * The Supabase `.remove()` fallback path (deleteFiles → `media` bucket) only
 * succeeds against NEW-shape company-scoped public-imagery paths
 * (`media_delete_company_scoped_public_folders` policy) — legacy-shape
 * objects remain service_role-delete-only. This function never touches the
 * private `documents` bucket.
 */
export async function deleteFilesGated(
  urls: string[],
  folder: StorageFolder,
): Promise<void> {
  if (urls.length === 0) return;

  // Either S3 flag means S3/CDN URLs can exist for these folders (images via
  // USE_S3_PROPERTY_IMAGES, videos via USE_S3_PROPERTY_VIDEOS) — route them
  // to the NestJS DELETE endpoint; Supabase URLs still delete via Supabase.
  if ((USE_S3_PROPERTY_IMAGES || USE_S3_PROPERTY_VIDEOS) && S3_IMAGE_FOLDERS.has(folder)) {
    const s3Urls = urls.filter(isS3CdnUrl);
    const supabaseUrls = urls.filter((url) => !isS3CdnUrl(url));
    if (s3Urls.length > 0) await deletePropertyMediaViaApi(s3Urls);
    if (supabaseUrls.length > 0) await deleteFiles(supabaseUrls);
    return;
  }
  await deleteFiles(urls);
}

/**
 * Maps a StorageError (or any thrown value) to a bilingual user-facing
 * message. Mirrors the pattern of getSupabaseErrorMessage in
 * src/lib/supabase-errors.ts: data layer carries codes, presentation layer
 * picks the right copy.
 *
 * Usage:
 *   try { await uploadImages(...) }
 *   catch (e) {
 *     const msg = getStorageErrorMessage(e, { ar: "فشل الرفع", en: "Upload failed" });
 *     toast.error(t(msg.ar, msg.en));
 *   }
 */
export function getStorageErrorMessage(
  error: unknown,
  fallback: { ar: string; en: string },
): { ar: string; en: string } {
  if (!(error instanceof StorageError)) {
    if (error instanceof Error && error.message) {
      return { ar: error.message, en: error.message };
    }
    return fallback;
  }

  const { code, details } = error;
  const name = details.fileName ?? "";
  const namePrefix = name ? `${name}: ` : "";

  switch (code) {
    case "FILE_TOO_LARGE": {
      const sizeMb = details.sizeBytes != null
        ? (details.sizeBytes / 1024 / 1024).toFixed(1)
        : "?";
      const limitMb = details.limitBytes != null
        ? (details.limitBytes / 1024 / 1024).toFixed(0)
        : "?";
      return {
        ar: `${namePrefix}حجم الملف ${sizeMb}MB أكبر من الحد المسموح ${limitMb}MB`,
        en: `${namePrefix}file size ${sizeMb}MB exceeds the ${limitMb}MB limit`,
      };
    }
    case "INVALID_MIME": {
      // HEIC/HEIF (iPhone's default photo format) is converted to JPEG
      // client-side before validation (see convertHeicIfNeeded above), so an
      // INVALID_MIME reaching here for a HEIC-flagged file means the
      // conversion itself failed (corrupt file, wasm load failure) — not
      // that the format is unsupported. Point the user at a manual
      // workaround instead of the generic "type not supported" copy.
      if (isHeicFile(details.fileType, name)) {
        return {
          ar: `${namePrefix}تعذّر تحويل صورة HEIC — جرّب تحويلها إلى JPG يدوياً`,
          en: `${namePrefix}Couldn't convert this HEIC photo — try converting it to JPG manually`,
        };
      }
      const type = details.fileType ?? "unknown";
      return {
        ar: `${namePrefix}نوع الملف "${type}" غير مدعوم. الأنواع المسموحة: JPG, PNG, WebP, GIF, PDF, MP4, MOV`,
        en: `${namePrefix}file type "${type}" not supported. Allowed: JPG, PNG, WebP, GIF, PDF, MP4, MOV`,
      };
    }
    case "DUPLICATE":
      return {
        ar: `${namePrefix}ملف بنفس الاسم موجود بالفعل`,
        en: `${namePrefix}a file with the same name already exists`,
      };
    case "INVALID_PATH":
      return {
        ar: "مسار الملف غير صالح",
        en: "Invalid storage path",
      };
    case "DELETE_FAILED":
      return {
        ar: "فشل حذف الملف" + (details.raw ? `: ${details.raw}` : ""),
        en: "Failed to delete file" + (details.raw ? `: ${details.raw}` : ""),
      };
    case "SIGN_FAILED":
      return {
        ar: "تعذّر إنشاء رابط عرض المستند" + (details.raw ? `: ${details.raw}` : ""),
        en: "Could not create a viewing link for the document" + (details.raw ? `: ${details.raw}` : ""),
      };
    case "UPLOAD_FAILED":
    default: {
      const raw = details.raw ?? error.message;
      return {
        ar: `${namePrefix}${raw || fallback.ar}`,
        en: `${namePrefix}${raw || fallback.en}`,
      };
    }
  }
}
